/**
 * Documentos / Plantillas — reemplaza la familia ares25xx del escritorio
 * (Pagaré Notarial, Carta Saldo, Acto de Venta, etc.) con un editor web
 * (TipTap, mismo editor que el módulo de plantillas de bankivo):
 *
 *   1. Redactas la plantilla directamente en el editor (o importas un Word
 *      existente, convertido en el navegador con mammoth). Admite imágenes
 *      redimensionables: logo de la empresa, sellos, firmas.
 *   2. Las variables se insertan con un clic donde esté el cursor y quedan
 *      como chips atómicos {{variable}} que no se rompen al editar.
 *   3. Generas el documento para cualquier préstamo: imprimir / guardar como
 *      PDF desde el navegador, o descargar Word (conversión aproximada).
 */
import React, { useState, useEffect, useRef, useMemo } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Chip from "@mui/material/Chip";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Divider from "@mui/material/Divider";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import SaveIcon from "@mui/icons-material/Save";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx, fpDataGridSx } from "components/financepro";
import { useApi } from "lib/api";
import { useMultiempresa, nombreEmpresa } from "lib/multiempresa";
import TiptapEditor from "./TiptapEditor";

const filtrarPrestamos = createFilterOptions({
  stringify: (o) =>
    typeof o === "string" ? o : `${o.noprest} ${o.nombres || ""} ${o.cedula || ""}`,
  limit: 60,
});

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});
const btnGhost = {
  ...btnBase,
  color: FP.color.textSecondary,
  bgcolor: "transparent",
  "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.textPrimary },
};

// Dentro del hub de Configuración el marco lo pone el panel derecho: la
// sección va sin tarjeta propia, solo con separación entre bloques.
const sectionBoxHub = { mb: 3 };

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function SectionTitle({ icon: Icon, children, color }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${color}14`,
          color,
        }}
      >
        <Icon sx={{ fontSize: 19 }} />
      </Box>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: "0.74rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: FP.color.textSecondary,
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Sin plantillas — crea la primera con &quot;Nueva plantilla&quot;
      </Typography>
    </Stack>
  );
}

// Descarga un archivo .docx desde base64.
export function descargarB64(b64, nombre) {
  const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
  const blob = new Blob([bytes], {
    type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nombre || "documento.docx";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

// Imprime el HTML generado. Estrategia principal: abrir el documento en una
// pestaña nueva (página de primer nivel, que Chrome imprime con el layout
// correcto — flotados y logos incluidos) donde un script espera las imágenes
// y dispara print(). Si el navegador bloquea la pestaña, respaldo con un
// iframe oculto de tamaño carta. Los estilos son los mismos del editor
// (TipTap) para que lo impreso se vea igual que lo redactado.
// Exportada: el expediente del préstamo genera estos mismos documentos y
// debe imprimirlos idéntico (una sola rutina de impresión para plantillas).
export function imprimirHtml(html, titulo) {
  // Chrome RECORTA al imprimir todo lo que se salga del área de contenido de
  // @page: los márgenes negativos del "borde a borde" funcionan en pantalla
  // pero en papel salían cortados y con el margen visible. Cuando el
  // documento trae un banner fullbleed, los márgenes horizontales se mueven
  // de @page al padding del body (constante en todas las páginas, y el banner
  // sí puede invadirlo) y el margen superior de la primera página se quita
  // con @page :first solo si el documento EMPIEZA con el banner.
  const tieneFullbleed = /data-fullbleed="true"/.test(html);
  const empiezaConBanner = /^\s*<img[^>]*data-fullbleed="true"/.test(String(html || "").trim());

  // Geometría de la hoja: la manda el backend como marcador .doc-config
  // (tamaño carta/legal/a4 y márgenes "sup,der,inf,izq" en mm). Documentos
  // viejos sin marcador: carta con 18mm, como siempre.
  const HOJAS = {
    carta: { css: "letter", ancho: "8.5in", alto: "11in" },
    legal: { css: "legal", ancho: "8.5in", alto: "14in" },
    a4: { css: "A4", ancho: "210mm", alto: "297mm" },
  };
  let cuerpoBase = String(html || "");
  let hoja = HOJAS.carta;
  let msup = 18;
  let mder = 18;
  let minf = 18;
  let mizq = 18;
  const mConf = cuerpoBase.match(
    /<div class="doc-config" data-hoja="([a-z0-9]+)" data-margenes="([0-9.,]+)"><\/div>/
  );
  if (mConf) {
    cuerpoBase = cuerpoBase.split(mConf[0]).join("");
    hoja = HOJAS[mConf[1]] || HOJAS.carta;
    const partes = mConf[2].split(",").map((v) => parseInt(v, 10));
    if (partes.length === 4 && partes.every((v) => Number.isFinite(v))) {
      [msup, mder, minf, mizq] = partes;
    }
  }
  const mh = mizq + mder; // margen horizontal total (offsets del borde a borde)

  // Numeración de páginas: el backend agrega un marcador cuando la plantilla
  // la pide; se detecta, se retira del cuerpo y se activa el @page margin box
  // (Chrome 131+; en navegadores viejos simplemente no sale el número).
  const marcaNumerar = '<div class="doc-flag-numerar"></div>';
  const numerarPaginas = cuerpoBase.includes(marcaNumerar);
  if (numerarPaginas) cuerpoBase = cuerpoBase.split(marcaNumerar).join("");
  const cssNumerar = numerarPaginas
    ? `@page{ @bottom-right{ content: "Página " counter(page) " de " counter(pages);
        font-family: Arial, sans-serif; font-size: 10px; color: #4a5866; } }`
    : "";

  // Pie de página: el backend lo agrega al final como <div class="doc-footer">.
  // Aquí se separa y se monta como tfoot de una tabla que envuelve el cuerpo:
  // es el único mecanismo que Chrome repite al final de CADA hoja impresa.
  const marcaPie = '<div class="doc-footer">';
  let cuerpo = cuerpoBase;
  let pieHtml = "";
  const posPie = cuerpo.lastIndexOf(marcaPie);
  if (posPie !== -1) {
    pieHtml = cuerpo.slice(posPie);
    cuerpo = cuerpo.slice(0, posPie);
  }
  const contenidoBody = pieHtml
    ? `<table class="doc-tabla"><tfoot><tr><td>${pieHtml}</td></tr></tfoot>` +
      `<tbody><tr><td>${cuerpo}</td></tr></tbody></table>`
    : cuerpo;
  const cssPagina = tieneFullbleed
    ? `@page{size:${hoja.css};margin:${msup}mm 0 ${minf}mm 0}
        ${empiezaConBanner ? "@page :first{margin-top:0}" : ""}
        body{margin:0;padding:0 ${mder}mm 0 ${mizq}mm;max-width:none}
        img[data-fullbleed="true"]:first-child{margin-top:${empiezaConBanner ? "0" : "0.5em"}}`
    : `@page{size:${hoja.css};margin:${msup}mm ${mder}mm ${minf}mm ${mizq}mm}
        body{margin:0;padding:0;max-width:none}`;
  const pagina = `
    <html><head><meta charset="utf-8"><meta name="color-scheme" content="light"><title>${
      titulo || "Documento"
    }</title>
    <style>
      body{font-family:Arial,sans-serif;font-size:14px;line-height:1.6;color:#000;
           max-width:${hoja.ancho};margin:0 auto;box-sizing:border-box;background:#fff;
           padding:${msup}mm ${mder}mm ${minf}mm ${mizq}mm}
      p{margin:0 0 0.75em}
      /* El editor (ProseMirror) trabaja con white-space:pre-wrap: las rachas
         de espacios (líneas de firma "____    ____") existen en el HTML
         guardado y deben verse igual al imprimir. */
      p,h1,h2,h3,h4,h5,h6,li,td,th{white-space:pre-wrap}
      /* Los párrafos vacíos del editor conservan su línea al imprimir
         (TipTap los serializa como <p></p>, que de otro modo colapsa). */
      p:empty::after{content:'\\00A0'}
      /* Alineación de plantillas guardadas con el editor anterior (Quill). */
      .ql-align-center{text-align:center}
      .ql-align-right{text-align:right}
      .ql-align-justify{text-align:justify}
      .ql-indent-1{padding-left:3em}
      .ql-indent-2{padding-left:6em}
      .ql-indent-3{padding-left:9em}
      h1{font-size:1.6em;font-weight:700;margin:0.5em 0}
      h2{font-size:1.35em;font-weight:700;margin:0.5em 0}
      h3{font-size:1.15em;font-weight:700;margin:0.5em 0}
      ul,ol{padding-left:1.5em}
      img{max-width:100%;height:auto}
      img[data-align="center"]{display:block;margin:0.5em auto}
      img[data-align="right"]{display:block;margin:0.5em 0 0.5em auto}
      img[data-align="left"]{display:block;margin:0.5em auto 0.5em 0}
      img[data-wrap="left"]{float:left;margin:0.3em 1em 0.5em 0}
      img[data-wrap="right"]{float:right;margin:0.3em 0 0.5em 1em}
      img[data-fullwidth="true"]{display:block;width:100%;height:auto;margin:0.5em 0}
      /* Borde a borde: compensa los márgenes de la hoja (padding del body en
         pantalla, margen de @page al imprimir) para tocar los bordes del
         papel; como primer elemento también el borde superior. */
      img[data-fullbleed="true"]{display:block;width:calc(100% + ${mh}mm);max-width:none;
        height:auto;margin:0.5em -${mder}mm 0.5em -${mizq}mm}
      img[data-fullbleed="true"]:first-child{margin-top:-${msup}mm}
      /* Tablas de CONTENIDO (insertadas en la plantilla): con bordes, como
         en el editor. La .doc-tabla envolvente del pie queda excluida. */
      table:not(.doc-tabla){border-collapse:collapse;width:100%;table-layout:fixed;margin:0.5em 0}
      table:not(.doc-tabla) td,table:not(.doc-tabla) th{border:1px solid #444;
        padding:4px 8px;vertical-align:top;word-wrap:break-word}
      table:not(.doc-tabla) th{background:#f1f3f4;font-weight:700}
      /* Tabla sin bordes (maquetación: firmas en columnas). */
      table[data-sinbordes="true"] td,table[data-sinbordes="true"] th{border:none;background:none}
      /* Tabla envolvente del pie de página: invisible, solo estructura. */
      table.doc-tabla{width:100%;border-collapse:collapse}
      table.doc-tabla > tbody > tr > td,
      table.doc-tabla > tfoot > tr > td{padding:0;border:0;vertical-align:top}
      .doc-footer{margin-top:1.6em;font-size:0.85em;color:#2a3440}
      .doc-footer p{margin:0 0 0.3em}
      @media print{
        ${cssPagina}
        ${cssNumerar}
        p,li{orphans:3;widows:3}
        h1,h2,h3,h4,h5,h6{page-break-after:avoid;break-after:avoid;
          page-break-inside:avoid;break-inside:avoid}
        table,figure,img{page-break-inside:avoid;break-inside:avoid}
        tr,td,th{page-break-inside:avoid;break-inside:avoid}
        /* La tabla envolvente del pie SÍ debe partirse entre hojas (su tbody
           es el documento completo); sin esto el contenido salía en una sola
           hoja recortada. El tfoot se repite al final de cada hoja. */
        table.doc-tabla,table.doc-tabla > tbody,table.doc-tabla > tbody > tr,
        table.doc-tabla > tbody > tr > td{page-break-inside:auto;break-inside:auto}
        /* La altura de la tabla llena la hoja (height en tablas actúa como
           mínimo): el pie queda pegado al FONDO de la hoja aunque el
           contenido sea corto, en vez de flotar tras el último párrafo. */
        table.doc-tabla{height:calc(${hoja.alto} - ${
    empiezaConBanner ? minf : msup + minf
  }mm - 2px)}
        thead{display:table-header-group}
        tfoot{display:table-footer-group}
        .page-break,[data-page-break="true"]{page-break-before:always;break-before:page}
      }
    </style></head>
    <body style="background:#fff;color:#000;background:#fff;color:#000">${contenidoBody}
    <script>
      (function () {
        var lanzado = false;
        function lanzar() {
          if (lanzado) return;
          lanzado = true;
          // Pequeño respiro para que termine el layout con las imágenes ya
          // decodificadas; sin esto el logo puede salir en blanco en el PDF.
          setTimeout(function () { window.focus(); window.print(); }, 150);
        }
        window.addEventListener('load', function () {
          var pendientes = Array.prototype.filter.call(document.images, function (im) {
            return !im.complete;
          });
          if (!pendientes.length) return lanzar();
          var falta = pendientes.length;
          pendientes.forEach(function (im) {
            im.onload = im.onerror = function () { if (--falta === 0) lanzar(); };
          });
          setTimeout(lanzar, 3000); // tope por si una imagen nunca carga
        });
      })();
    <\/script>
    </body></html>`;

  // Pestaña nueva: el documento se imprime como página de primer nivel, con
  // el layout completo (flotados "texto al lado", logos). Además el usuario
  // ve el documento y puede reimprimirlo con Ctrl+P.
  const blob = new Blob([pagina], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const w = window.open(url, "_blank");
  if (w) {
    setTimeout(() => URL.revokeObjectURL(url), 60000);
    return;
  }
  URL.revokeObjectURL(url);

  // Respaldo si el navegador bloqueó la pestaña: iframe oculto de tamaño
  // carta (nunca 0x0: con ancho cero los flotados salen descuadrados). El
  // script embebido de la página dispara print() dentro del iframe.
  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.cssText = `position:fixed;left:-9999px;top:0;width:${hoja.ancho};height:${hoja.alto};border:0;`;
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(pagina);
  doc.close();
  setTimeout(() => iframe.remove(), 60000);
}

// Tamaños de hoja del editor (mismas llaves que el backend y la impresión).
const HOJAS_EDITOR = {
  carta: { ancho: "8.5in", alto: "11in", etiqueta: "Carta (8.5 × 11)" },
  legal: { ancho: "8.5in", alto: "14in", etiqueta: "Legal (8.5 × 14)" },
  a4: { ancho: "210mm", alto: "297mm", etiqueta: "A4 (210 × 297mm)" },
};
const MARGENES_DEF = { sup: 18, der: 18, inf: 18, izq: 18 };

// Origen de los datos de la plantilla. Decide qué variables ofrece el editor
// y desde dónde se genera: las de préstamo se usan en esta misma pantalla; las
// de x mayor, desde el detalle de la factura mayorista; las de contado, desde
// el listado de motores facturados (pantalla Ventas o el informe).
export const TIPOS_PLANTILLA = [
  { valor: "prestamo", etiqueta: "Préstamo" },
  { valor: "xmayor", etiqueta: "Venta x mayor" },
  { valor: "contado", etiqueta: "Venta al contado" },
];
const etiquetaTipo = (t) => TIPOS_PLANTILLA.find((x) => x.valor === t)?.etiqueta || "Préstamo";

// Condición de emisión (solo origen "préstamo"); el backend la hace cumplir al
// generar. "aldia" = sin cuotas vencidas por encima del atraso tolerado (carta
// de ruta); "saldado" = préstamo cancelado con balance cero (carta de saldo).
// Vacío = se genera siempre, como hasta ahora.
export const CONDICIONES_PLANTILLA = [
  { valor: "", etiqueta: "Sin restricción" },
  { valor: "aldia", etiqueta: "Préstamo al día" },
  { valor: "saldado", etiqueta: "Préstamo saldado" },
];
const etiquetaCondicion = (c) =>
  CONDICIONES_PLANTILLA.find((x) => x.valor === (c || ""))?.etiqueta || "Sin restricción";

const EDITOR_INICIAL = {
  id: null,
  nombre: "",
  descripcion: "",
  contenido: "",
  pie: "",
  numerarpaginas: false,
  tamhoja: "carta",
  margenes: MARGENES_DEF,
  // Multi-empresa: null = compartida (todas las empresas); N = solo esa.
  empresa: null,
  tipo: "prestamo",
  // Condición de emisión y atraso tolerado en RD$ (solo con "aldia").
  condicion: "",
  atrasomax: 0,
  // Documento del portal de clientes que representa ("cartaruta" /
  // "cartasaldo"); vacío = no se ofrece en el portal.
  portal: "",
};

// Documentos que el cliente puede pedir desde el portal. El sistema no sabe
// qué plantilla es cada carta: se marca aquí y el backend la usa (con su
// condición de emisión) cuando el cliente la pide.
export const PORTAL_PLANTILLA = [
  { valor: "", etiqueta: "No se ofrece" },
  { valor: "cartaruta", etiqueta: "Como carta de ruta" },
  { valor: "cartasaldo", etiqueta: "Como carta de saldo" },
];
const etiquetaPortal = (v) => PORTAL_PLANTILLA.find((x) => x.valor === (v || ""))?.etiqueta || "";

export default function Documentos({ enHub } = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  // Multi-empresa: con el modo activo cada plantilla puede pertenecer a una
  // empresa (o ser compartida) y al generar solo se ofrecen las del préstamo.
  const multiempresa = useMultiempresa();

  // ---- Catálogo de variables ----
  // Uno por origen de datos: una plantilla de préstamo ofrece {{fiador}} e
  // {{inicial}}, y una de x mayor la tabla {{listachasis}}. Se cargan los dos
  // al montar (son dos listas estáticas) y el panel muestra el del tipo que
  // se esté editando.
  const [variablesPorTipo, setVariablesPorTipo] = useState({});
  useEffect(() => {
    let activo = true;
    TIPOS_PLANTILLA.forEach(({ valor }) => {
      post("/api/documentos_variables", { tipo: valor }).then(({ ok, data }) => {
        if (activo && ok && Array.isArray(data.variables)) {
          setVariablesPorTipo((p) => ({ ...p, [valor]: data.variables }));
        }
      });
    });
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Plantillas ----
  const [plantillas, setPlantillas] = useState([]);
  const [cargandoPl, setCargandoPl] = useState(false);

  const cargarPlantillas = () => {
    setCargandoPl(true);
    return post("/api/documentos_plantillas", {})
      .then(({ ok, data }) => {
        if (ok && Array.isArray(data.plantillas)) setPlantillas(data.plantillas);
      })
      .finally(() => setCargandoPl(false));
  };

  // ---- Préstamos (todos, filtro local) ----
  const [prestamos, setPrestamos] = useState([]);
  useEffect(() => {
    cargarPlantillas();
    post("/api/documentos_prestamos", {}).then(({ ok, data }) => {
      if (ok && Array.isArray(data.prestamos)) setPrestamos(data.prestamos);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Generar ----
  const [plantillaSel, setPlantillaSel] = useState("");
  const [prestamoSel, setPrestamoSel] = useState(null);
  const [generando, setGenerando] = useState(""); // "" | "imprimir" | "word"

  // En multi-empresa, elegido el préstamo solo se ofrecen las plantillas de
  // su empresa más las compartidas. Con un noprest tecleado a mano (freeSolo)
  // no se conoce la empresa: se ofrecen todas y el backend valida al generar.
  const empresaPrestamo =
    multiempresa.activo && prestamoSel && typeof prestamoSel !== "string"
      ? prestamoSel.empresa || 1
      : null;
  // Aquí solo se generan documentos de PRÉSTAMO: los de venta x mayor salen
  // del detalle de la factura y los de contado del listado de motores
  // facturados, que son quienes saben el número de factura.
  const plantillasGenerar = useMemo(() => {
    const dePrestamo = plantillas.filter((p) => (p.tipo || "prestamo") === "prestamo");
    return empresaPrestamo == null
      ? dePrestamo
      : dePrestamo.filter(
          (p) => p.empresa == null || String(p.empresa) === String(empresaPrestamo)
        );
  }, [plantillas, empresaPrestamo]);
  // Si el préstamo elegido deja fuera la plantilla seleccionada, se limpia.
  useEffect(() => {
    if (plantillaSel && !plantillasGenerar.some((p) => p.id === plantillaSel)) {
      setPlantillaSel("");
    }
  }, [plantillasGenerar, plantillaSel]);

  const generar = async (modo) => {
    const noprest = typeof prestamoSel === "string" ? prestamoSel.trim() : prestamoSel?.noprest;
    if (!plantillaSel) {
      showAlert({ message: "Seleccione una plantilla", color: "warning" });
      return;
    }
    if (!noprest) {
      showAlert({ message: "Seleccione un préstamo", color: "warning" });
      return;
    }
    setGenerando(modo);
    try {
      const { ok, data } = await post("/api/documentos_generar", {
        id: plantillaSel,
        noprest: String(noprest),
      });
      if (!ok) {
        showAlert({ message: data.Error || "No se pudo generar el documento", color: "error" });
        return;
      }
      if (modo === "imprimir") {
        imprimirHtml(data.html || "", data.nombre);
      } else if (data.archivo_b64) {
        descargarB64(data.archivo_b64, data.nombre);
      } else {
        showAlert({
          message: "No se pudo convertir a Word; use Imprimir / PDF",
          color: "warning",
        });
      }
    } finally {
      setGenerando("");
    }
  };

  // ---- Editor ----
  const [editorOpen, setEditorOpen] = useState(false);
  const [editor, setEditor] = useState(EDITOR_INICIAL);
  const [guardando, setGuardando] = useState(false);
  const [cargandoEditor, setCargandoEditor] = useState(false);
  const [busquedaVariable, setBusquedaVariable] = useState("");
  const editorRef = useRef(null);
  const pieRef = useRef(null);
  // Último editor que tuvo el foco ("cuerpo" | "pie"): el panel de variables
  // inserta ahí, porque al hacer clic en la variable el editor pierde el foco.
  const editorActivoRef = useRef("cuerpo");
  // Alto (px) que el pie ocupará en cada hoja impresa; el editor principal lo
  // descuenta en sus guías "fin de hoja". El 0.85 replica el font-size con el
  // que la impresión reduce el pie.
  const [reservaPie, setReservaPie] = useState(0);
  const medirPie = () => {
    const texto = pieRef.current?.getText?.() || "";
    const html = pieRef.current?.getHTML?.() || "";
    const vacio = texto.trim() === "" && !html.includes("<img");
    const alto = vacio ? 0 : Math.round((pieRef.current?.getAlturaContenido?.() || 0) * 0.85);
    setReservaPie(alto);
  };

  const nuevaPlantilla = () => {
    setEditor(EDITOR_INICIAL);
    setBusquedaVariable("");
    editorActivoRef.current = "cuerpo";
    setEditorOpen(true);
    setTimeout(() => {
      editorRef.current?.setContent("");
      pieRef.current?.setContent("");
      medirPie();
    }, 50);
  };

  const editarPlantilla = async (id) => {
    setCargandoEditor(true);
    try {
      const { ok, data } = await post("/api/documentos_plantilla", { id });
      if (!ok || !data.plantilla) {
        showAlert({ message: data.Error || "No se pudo cargar la plantilla", color: "error" });
        return;
      }
      const contenido = data.plantilla.contenido || "";
      const pie = data.plantilla.pie || "";
      const partesM = String(data.plantilla.margenes || "18,18,18,18")
        .split(",")
        .map((v) => parseInt(v, 10));
      setEditor({
        id: data.plantilla.id,
        nombre: data.plantilla.nombre || "",
        descripcion: data.plantilla.descripcion || "",
        contenido,
        pie,
        numerarpaginas: !!data.plantilla.numerarpaginas,
        empresa: data.plantilla.empresa ?? null,
        tipo: data.plantilla.tipo || "prestamo",
        condicion: data.plantilla.condicion || "",
        atrasomax: Number(data.plantilla.atrasomax) || 0,
        portal: data.plantilla.portal || "",
        tamhoja: HOJAS_EDITOR[data.plantilla.tamhoja] ? data.plantilla.tamhoja : "carta",
        margenes:
          partesM.length === 4 && partesM.every((v) => Number.isFinite(v))
            ? { sup: partesM[0], der: partesM[1], inf: partesM[2], izq: partesM[3] }
            : MARGENES_DEF,
      });
      setBusquedaVariable("");
      editorActivoRef.current = "cuerpo";
      setEditorOpen(true);
      setTimeout(() => {
        editorRef.current?.setContent(contenido);
        pieRef.current?.setContent(pie);
      }, 50);
      // Tras cargar: medir el pie ya renderizado (imágenes incluidas).
      setTimeout(medirPie, 450);
    } finally {
      setCargandoEditor(false);
    }
  };

  // Inserta la variable como chip atómico donde esté el cursor, en el cuerpo
  // o en el pie de página según cuál tuvo el foco por última vez.
  const insertarVariable = (v) => {
    const destino = editorActivoRef.current === "pie" ? pieRef : editorRef;
    destino.current?.insertVariable(v.nombre, v.descripcion);
  };

  // Alertas emitidas desde el editor (import de Word, imágenes).
  const alertaEditor = (message, severity) => {
    showAlert({ message, color: severity || "info" });
  };

  // Variables del origen que se está editando.
  const variables = variablesPorTipo[editor.tipo] || [];

  const variablesFiltradas = useMemo(() => {
    const q = busquedaVariable.trim().toLowerCase();
    if (!q) return variables;
    return variables.filter(
      (v) => v.nombre.toLowerCase().includes(q) || (v.descripcion || "").toLowerCase().includes(q)
    );
  }, [variables, busquedaVariable]);

  // Agrupadas por categoría para que el panel sea más legible.
  const variablesAgrupadas = useMemo(() => {
    const grupos = new Map();
    variablesFiltradas.forEach((v) => {
      const cat = v.categoria || "Variables del préstamo";
      if (!grupos.has(cat)) grupos.set(cat, []);
      grupos.get(cat).push(v);
    });
    return Array.from(grupos.entries());
  }, [variablesFiltradas]);

  const guardarPlantilla = async () => {
    if (!editor.nombre.trim()) {
      showAlert({ message: "Indique el nombre de la plantilla", color: "warning" });
      return;
    }
    const html = editorRef.current?.getHTML() || "";
    const texto = editorRef.current?.getText() || "";
    if (texto.trim() === "" && !html.includes("<img")) {
      showAlert({ message: "La plantilla está vacía", color: "warning" });
      return;
    }
    // Pie de página: vacío si solo tiene el párrafo en blanco del editor.
    const pieHtml = pieRef.current?.getHTML() || "";
    const pieTexto = pieRef.current?.getText() || "";
    const pie = pieTexto.trim() === "" && !pieHtml.includes("<img") ? "" : pieHtml;
    setGuardando(true);
    try {
      const { ok, data } = await post("/api/documentos_plantilla_guardar", {
        id: editor.id,
        nombre: editor.nombre.trim(),
        descripcion: editor.descripcion.trim(),
        contenido: html,
        pie,
        numerarpaginas: editor.numerarpaginas,
        empresa: editor.empresa,
        tipo: editor.tipo,
        condicion: editor.tipo === "prestamo" ? editor.condicion : "",
        atrasomax: editor.condicion === "aldia" ? editor.atrasomax : 0,
        portal: editor.tipo === "prestamo" ? editor.portal : "",
        tamhoja: editor.tamhoja,
        margenes: [
          editor.margenes.sup,
          editor.margenes.der,
          editor.margenes.inf,
          editor.margenes.izq,
        ].join(","),
      });
      if (ok) {
        showAlert({ message: data.mensajeok || "Plantilla guardada", color: "success" });
        setEditorOpen(false);
        cargarPlantillas();
      } else {
        showAlert({ message: data.Error || "No se pudo guardar", color: "error" });
      }
    } finally {
      setGuardando(false);
    }
  };

  // ---- Eliminar ----
  const [target, setTarget] = useState(null);
  const [eliminando, setEliminando] = useState(false);

  const eliminar = async () => {
    setEliminando(true);
    try {
      const { ok, data } = await post("/api/documentos_plantilla_eliminar", { id: target.id });
      if (ok) {
        showAlert({ message: "Plantilla eliminada", color: "success" });
        setTarget(null);
        cargarPlantillas();
      } else {
        showAlert({ message: data.Error || "No se pudo eliminar", color: "error" });
      }
    } finally {
      setEliminando(false);
    }
  };

  const columns = [
    { field: "nombre", headerName: "Plantilla", flex: 1, minWidth: 200 },
    { field: "descripcion", headerName: "Descripción", flex: 1, minWidth: 200 },
    {
      field: "tipo",
      headerName: "Origen",
      width: 130,
      valueGetter: (value) => etiquetaTipo(value),
    },
    {
      field: "condicion",
      headerName: "Emisión",
      width: 170,
      valueGetter: (value, row) => {
        if ((row.tipo || "prestamo") !== "prestamo") return "";
        const base = etiquetaCondicion(value);
        return value === "aldia" && Number(row.atrasomax) > 0
          ? `${base} (hasta RD$ ${Number(row.atrasomax).toLocaleString("es-DO")})`
          : base;
      },
    },
    {
      field: "portal",
      headerName: "Portal",
      width: 160,
      valueGetter: (value, row) =>
        (row.tipo || "prestamo") !== "prestamo" || !value ? "" : etiquetaPortal(value),
    },
    ...(multiempresa.activo
      ? [
          {
            field: "empresa",
            headerName: "Empresa",
            width: 160,
            valueGetter: (_v, row) =>
              row.empresa ? nombreEmpresa(multiempresa.empresas, row.empresa) : "Todas",
          },
        ]
      : []),
    { field: "fecha_mod", headerName: "Modificada", width: 130 },
    {
      field: "acciones",
      headerName: "",
      width: 230,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.8}>
          <Button
            size="small"
            startIcon={<EditOutlinedIcon />}
            onClick={() => editarPlantilla(params.row.id)}
            sx={btnOutline(FP.color.navy)}
          >
            Editar
          </Button>
          <Button
            size="small"
            startIcon={<DeleteOutlineIcon />}
            onClick={() => setTarget(params.row)}
            sx={btnOutline(FP.color.danger)}
          >
            Eliminar
          </Button>
        </Stack>
      ),
    },
  ];

  return (
    <Box
      component="main"
      sx={enHub ? undefined : { minHeight: "100vh", bgcolor: FP.color.surface }}
    >
      <PageHeader
        compact={enHub}
        title="Documentos"
        subtitle="Plantillas con editor: pagarés, cartas de saldo, actos de venta y cualquier documento con datos del préstamo"
        cta={
          <Button
            startIcon={<AddIcon />}
            onClick={nuevaPlantilla}
            sx={btnSolid(FP.color.navy, FP.color.navySoft)}
          >
            Nueva plantilla
          </Button>
        }
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Generar documento */}
        <Box sx={enHub ? sectionBoxHub : sectionBox}>
          <SectionTitle icon={DescriptionOutlinedIcon} color={FP.color.navy}>
            Generar Documento
          </SectionTitle>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                select
                fullWidth
                label="Plantilla"
                value={plantillaSel}
                onChange={(e) => setPlantillaSel(e.target.value)}
                sx={fpInputSx}
              >
                {plantillasGenerar.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.nombre}
                    {multiempresa.activo && p.empresa
                      ? ` — ${nombreEmpresa(multiempresa.empresas, p.empresa)}`
                      : ""}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Autocomplete
                freeSolo
                autoHighlight
                selectOnFocus
                handleHomeEndKeys
                filterOptions={filtrarPrestamos}
                options={prestamos}
                value={prestamoSel}
                getOptionLabel={(o) =>
                  typeof o === "string" ? o : `${o.noprest} — ${o.nombres || ""}`
                }
                isOptionEqualToValue={(o, v) => (o.noprest || o) === (v.noprest || v)}
                onChange={(_, v) => setPrestamoSel(v)}
                renderOption={(props, o) => (
                  <li {...props} key={o.noprest}>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                        {o.noprest}
                      </Typography>
                      <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                        {[o.nombres, o.cedula].filter(Boolean).join(" · ")}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="Préstamo / cliente" sx={fpInputSx} />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Stack direction="row" spacing={1}>
                <Button
                  fullWidth
                  startIcon={
                    generando === "imprimir" ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <PrintOutlinedIcon />
                    )
                  }
                  onClick={() => generar("imprimir")}
                  disabled={!!generando}
                  sx={btnSolid(FP.color.navy, FP.color.navySoft)}
                >
                  Imprimir / PDF
                </Button>
                <Button
                  fullWidth
                  startIcon={
                    generando === "word" ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <DownloadOutlinedIcon />
                    )
                  }
                  onClick={() => generar("word")}
                  disabled={!!generando}
                  sx={btnOutline(FP.color.navy)}
                >
                  Word
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>

        {/* Plantillas */}
        <Box sx={{ ...sectionBox, mb: 0 }}>
          <SectionTitle icon={EditOutlinedIcon} color={FP.color.accentBlue}>
            Plantillas
          </SectionTitle>
          <Box sx={{ bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={plantillas}
              columns={columns}
              loading={cargandoPl || cargandoEditor}
              rowHeight={48}
              pageSizeOptions={[10, 25]}
              initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
              slots={{ noRowsOverlay: NoRowsOverlay }}
              sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "220px" }}
              disableRowSelectionOnClick
            />
          </Box>
        </Box>
      </Box>

      {/* Editor de plantilla — fullscreen, igual al editor de plantillas de
          bankivo: editor a la izquierda, panel de variables buscable a la
          derecha, guardado desde la barra superior. */}
      <Dialog
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        fullScreen
        PaperProps={{ sx: { backgroundColor: FP.color.surface } }}
      >
        <AppBar position="relative" elevation={0} sx={{ backgroundColor: FP.color.navy }}>
          <Toolbar>
            <Typography variant="h6" sx={{ flex: 1, fontWeight: 600, color: "#FFFFFF" }}>
              {editor.id ? "Editar plantilla" : "Nueva plantilla"}
            </Typography>
            <Button
              onClick={guardarPlantilla}
              variant="contained"
              disabled={guardando}
              startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              sx={{
                ...btnBase,
                bgcolor: "#FFFFFF",
                color: FP.color.navy,
                mr: 1,
                "&:hover": { bgcolor: "#F0F0F0", boxShadow: "none" },
              }}
            >
              {guardando ? "Guardando..." : editor.id ? "Actualizar" : "Crear"}
            </Button>
            <IconButton
              onClick={() => setEditorOpen(false)}
              disabled={guardando}
              sx={{ color: "white" }}
            >
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        <DialogContent sx={{ p: 2, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <Box sx={{ display: "flex", gap: 2, flex: 1, minHeight: 0 }}>
            {/* Panel izquierdo: datos + editor. Desplazable: la hoja principal
                conserva su altura grande y el pie de página queda debajo. */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                minWidth: 0,
                overflowY: "auto",
                pr: 0.5,
              }}
            >
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <TextField
                  label="Nombre de la Plantilla"
                  value={editor.nombre}
                  onChange={(e) => setEditor((p) => ({ ...p, nombre: e.target.value }))}
                  required
                  size="small"
                  sx={{ ...fpInputSx, flex: 2, minWidth: 220 }}
                />
                <TextField
                  label="Descripción"
                  value={editor.descripcion}
                  onChange={(e) => setEditor((p) => ({ ...p, descripcion: e.target.value }))}
                  size="small"
                  sx={{ ...fpInputSx, flex: 3, minWidth: 240 }}
                />
                {/* Origen de los datos: cambia las variables que ofrece el
                    panel y desde dónde se genera el documento. Se puede
                    cambiar sobre la marcha; las variables ya insertadas que no
                    existan en el otro origen quedan como marcador sin valor. */}
                <TextField
                  select
                  label="Origen de los datos"
                  value={editor.tipo}
                  onChange={(e) => setEditor((p) => ({ ...p, tipo: e.target.value }))}
                  size="small"
                  helperText={
                    {
                      xmayor: "Se genera desde el detalle de la factura x mayor",
                      contado: "Se genera desde el listado de motores facturados",
                    }[editor.tipo] || "Se genera en esta pantalla, eligiendo el préstamo"
                  }
                  sx={{ ...fpInputSx, flex: 2, minWidth: 220 }}
                >
                  {TIPOS_PLANTILLA.map((t) => (
                    <MenuItem key={t.valor} value={t.valor}>
                      {t.etiqueta}
                    </MenuItem>
                  ))}
                </TextField>
                {/* Multi-empresa: dueña de la plantilla. "Todas" = compartida;
                    con una empresa fija solo genera para sus préstamos (útil
                    cuando el logo o la redacción son de esa empresa). */}
                {multiempresa.activo && (
                  <TextField
                    select
                    label="Empresa"
                    value={editor.empresa == null ? "" : String(editor.empresa)}
                    onChange={(e) =>
                      setEditor((p) => ({
                        ...p,
                        empresa: e.target.value === "" ? null : Number(e.target.value),
                      }))
                    }
                    size="small"
                    sx={{ ...fpInputSx, flex: 1, minWidth: 200 }}
                  >
                    <MenuItem value="">Todas las empresas</MenuItem>
                    {multiempresa.empresas.map((e) => (
                      <MenuItem key={e.empresa} value={String(e.empresa)}>
                        {e.nombre || `Empresa ${e.empresa}`}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
                {/* Condición de emisión: la carta de ruta solo sale con el
                    préstamo al día (o con un atraso tolerado) y la carta de
                    saldo solo con el préstamo saldado. La valida el backend al
                    generar, desde cualquier pantalla. Solo aplica a datos de
                    préstamo. */}
                {editor.tipo === "prestamo" && (
                  <TextField
                    select
                    label="Condición de emisión"
                    value={editor.condicion}
                    onChange={(e) => setEditor((p) => ({ ...p, condicion: e.target.value }))}
                    size="small"
                    helperText={
                      {
                        aldia: "Solo si no hay cuotas vencidas por encima del atraso tolerado",
                        saldado: "Solo si el préstamo está cancelado con balance cero",
                      }[editor.condicion] || "Se genera siempre"
                    }
                    sx={{ ...fpInputSx, flex: 1, minWidth: 200 }}
                  >
                    {CONDICIONES_PLANTILLA.map((c) => (
                      <MenuItem key={c.valor || "libre"} value={c.valor}>
                        {c.etiqueta}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
                {editor.tipo === "prestamo" && editor.condicion === "aldia" && (
                  <TextField
                    type="number"
                    label="Atraso tolerado (RD$)"
                    value={editor.atrasomax}
                    onChange={(e) =>
                      setEditor((p) => ({
                        ...p,
                        atrasomax: Math.max(0, parseFloat(e.target.value) || 0),
                      }))
                    }
                    size="small"
                    helperText="0 = estrictamente al día"
                    inputProps={{ min: 0, step: "0.01" }}
                    sx={{ ...fpInputSx, width: 170 }}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
                {/* Portal de clientes: el cliente pide "carta de ruta" o "carta
                    de saldo" desde la app; aquí se dice qué plantilla es cada
                    una. Se emite al instante solo si cumple la condición de
                    emisión de arriba. */}
                {editor.tipo === "prestamo" && (
                  <TextField
                    select
                    label="Portal de clientes"
                    value={editor.portal}
                    onChange={(e) => setEditor((p) => ({ ...p, portal: e.target.value }))}
                    size="small"
                    helperText={
                      editor.portal
                        ? "El cliente la pide desde la app; se emite si cumple la condición"
                        : "No se ofrece en el portal de clientes"
                    }
                    sx={{ ...fpInputSx, flex: 1, minWidth: 200 }}
                  >
                    {PORTAL_PLANTILLA.map((c) => (
                      <MenuItem key={c.valor || "no"} value={c.valor}>
                        {c.etiqueta}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </Box>

              {/* Geometría de la hoja: tamaño y márgenes (mm), como Docs. */}
              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
                <TextField
                  select
                  label="Tamaño de hoja"
                  value={editor.tamhoja}
                  onChange={(e) => setEditor((p) => ({ ...p, tamhoja: e.target.value }))}
                  size="small"
                  SelectProps={{ native: true }}
                  sx={{ ...fpInputSx, minWidth: 170 }}
                  InputLabelProps={{ shrink: true }}
                >
                  {Object.entries(HOJAS_EDITOR).map(([clave, h]) => (
                    <option key={clave} value={clave}>
                      {h.etiqueta}
                    </option>
                  ))}
                </TextField>
                {[
                  ["sup", "M. superior"],
                  ["der", "M. derecho"],
                  ["inf", "M. inferior"],
                  ["izq", "M. izquierdo"],
                ].map(([clave, etiqueta]) => (
                  <TextField
                    key={clave}
                    type="number"
                    label={`${etiqueta} (mm)`}
                    value={editor.margenes[clave]}
                    onChange={(e) => {
                      const v = Math.max(0, Math.min(50, parseInt(e.target.value, 10) || 0));
                      setEditor((p) => ({ ...p, margenes: { ...p.margenes, [clave]: v } }));
                    }}
                    size="small"
                    sx={{ ...fpInputSx, width: 130 }}
                    InputLabelProps={{ shrink: true }}
                  />
                ))}
              </Box>

              {/* Altura mínima garantizada para la hoja: el editor principal
                  no se encoge por el editor del pie; este baja y se llega a él
                  con el scroll del panel. */}
              <Box
                sx={{ display: "flex", flexDirection: "column", minHeight: "74vh", flexShrink: 0 }}
              >
                <TiptapEditor
                  ref={editorRef}
                  initialContent={editor.contenido}
                  onChange={(html) => setEditor((p) => ({ ...p, contenido: html }))}
                  onAlert={alertaEditor}
                  onFocus={() => {
                    editorActivoRef.current = "cuerpo";
                  }}
                  reservaPie={reservaPie}
                  hoja={{ ...HOJAS_EDITOR[editor.tamhoja], margenes: editor.margenes }}
                />
              </Box>

              {/* Pie de página: se guarda aparte y al imprimir se repite al
                  final de cada hoja. Acepta variables {{...}} como el cuerpo. */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  mt: 0.5,
                }}
              >
                <Typography variant="subtitle2" fontWeight={700}>
                  Pie de página (opcional) — se repite al final de cada hoja impresa
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={editor.numerarpaginas}
                      onChange={(e) =>
                        setEditor((p) => ({ ...p, numerarpaginas: e.target.checked }))
                      }
                    />
                  }
                  label={
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      Numerar páginas al imprimir (Página N de M)
                    </Typography>
                  }
                />
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", flexShrink: 0, pb: 1 }}>
                <TiptapEditor
                  ref={pieRef}
                  initialContent={editor.pie}
                  onChange={(html) => {
                    setEditor((p) => ({ ...p, pie: html }));
                    // Al siguiente tick el DOM del pie ya tiene la altura nueva.
                    setTimeout(medirPie, 100);
                  }}
                  onAlert={alertaEditor}
                  onFocus={() => {
                    editorActivoRef.current = "pie";
                  }}
                  compacto
                  hoja={{ ...HOJAS_EDITOR[editor.tamhoja], margenes: editor.margenes }}
                />
              </Box>
            </Box>

            {/* Panel derecho: variables */}
            <Box
              sx={{
                width: 320,
                backgroundColor: "white",
                borderRadius: 1,
                border: `1px solid ${FP.color.border}`,
                p: 2,
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
              }}
            >
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Variables Disponibles
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5 }}>
                Haz clic en una variable para insertarla donde esté el cursor (cuerpo o pie de
                página)
              </Typography>

              <TextField
                fullWidth
                size="small"
                placeholder="Buscar variable..."
                value={busquedaVariable}
                onChange={(e) => setBusquedaVariable(e.target.value)}
                sx={{ ...fpInputSx, mb: 1.5 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />

              <Box sx={{ flex: 1, overflowY: "auto", pr: 0.5 }}>
                {variablesFiltradas.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: "center", py: 2 }}
                  >
                    No se encontraron variables
                  </Typography>
                ) : (
                  variablesAgrupadas.map(([categoria, vars]) => (
                    <Box key={categoria} sx={{ mb: 1.5 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                          fontWeight: 700,
                          color: FP.color.navy,
                          mb: 0.5,
                          mt: 0.5,
                        }}
                      >
                        {categoria}
                      </Typography>
                      {vars.map((v) => (
                        <Card
                          key={`${categoria}-${v.nombre}`}
                          sx={{ mb: 1, cursor: "pointer", "&:hover": { boxShadow: 2 } }}
                          onClick={() => insertarVariable(v)}
                        >
                          <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ minWidth: 0, fontWeight: 500 }}
                                noWrap
                                title={v.descripcion}
                              >
                                {v.descripcion}
                              </Typography>
                              <Chip
                                label={`{{${v.nombre}}}`}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ ml: 1, flexShrink: 0 }}
                              />
                            </Box>
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  ))
                )}
              </Box>

              <Divider sx={{ my: 1.5 }} />

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Instrucciones
                </Typography>
                <Typography variant="caption" color="text.secondary" component="div">
                  • Usa <strong>Cargar Word</strong> para importar un .docx existente.
                  <br />• Las variables {"{{...}}"} dentro del Word se detectan automáticamente.
                  <br />
                  • Inserta variables haciendo clic en la lista.
                  <br />• Tamaño máximo de imagen: 2MB. Word: 10MB.
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Eliminar */}
      <Dialog
        open={!!target}
        onClose={() => setTarget(null)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Eliminar plantilla</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Eliminar la plantilla &quot;{target?.nombre}&quot;? Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setTarget(null)} sx={btnGhost}>
            No
          </Button>
          <Button
            startIcon={
              eliminando ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />
            }
            onClick={eliminar}
            disabled={eliminando}
            sx={btnSolid("#D32F2F", "#B71C1C")}
          >
            Sí, eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
